import dtfIndexAbi from '@/abis/dtf-index-abi'
import dtfIndexAbiV2 from '@/abis/dtf-index-abi-v2'
import { TransactionButtonContainer } from '@/components/ui/transaction-button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import Spinner from '@/components/ui/spinner'
import { chainIdAtom, walletAtom } from '@/state/atoms'
import { indexDTFAtom, indexDTFVersionAtom } from '@/state/dtf/atoms'
import { formatCurrency, max, safeParseEther } from '@/utils'
import { ROUTES } from '@/utils/constants'
import { atom, useAtom, useAtomValue } from 'jotai'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Address, parseEther } from 'viem'
import { useWaitForTransactionReceipt, useWriteContract } from 'wagmi'
import {
  allowanceMapAtom,
  amountAtom,
  assetAmountsMapAtom,
  balanceMapAtom,
  modeAtom,
  tokensNeedingApprovalAtom,
} from '../atoms'
import ApproveAllButton from './approve-all-button'
import InputBox from './input-box'
import ModeSelector from './mode-selector'
import { useTrackIndexDTFClick } from '@/views/index-dtf/hooks/useTrackIndexDTFPage'

const getErrorMessage = (error: Error) => {
  const messageSplit = error.message.split('\n')
  const message =
    messageSplit.length > 1
      ? messageSplit[0] + ' ' + messageSplit[1]
      : (messageSplit[0] ?? '')

  return message
}

const VALIDATION_ERRORS = {
  BALANCE: 'Insufficient balance',
  ALLOWANCE: 'Need to approve use of tokens',
}

const isValidAtom = atom<[boolean, string]>((get) => {
  const mode = get(modeAtom)
  const amount = get(amountAtom)
  const indexDTF = get(indexDTFAtom)
  const balanceMap = get(balanceMapAtom)
  const allowanceMap = get(allowanceMapAtom)
  const requiredAmounts = get(assetAmountsMapAtom)

  if (
    !indexDTF ||
    isNaN(Number(amount)) ||
    Number(amount) <= 0 ||
    !Object.keys(balanceMap).length ||
    !Object.keys(allowanceMap).length ||
    !Object.keys(requiredAmounts).length
  ) {
    return [false, '']
  }

  // Simple case, on redeem just check if the balance is enough
  if (mode === 'sell') {
    const isValid = balanceMap[indexDTF.id] >= safeParseEther(amount)
    return [isValid, isValid ? '' : VALIDATION_ERRORS.BALANCE]
  }

  // On mint, check if the allowance and balance is enough for each asset
  for (const asset of Object.keys(requiredAmounts)) {
    if ((balanceMap[asset] ?? 0n) < requiredAmounts[asset]) {
      return [false, VALIDATION_ERRORS.BALANCE]
    }
    if ((allowanceMap[asset] ?? 0n) < requiredAmounts[asset]) {
      return [false, VALIDATION_ERRORS.ALLOWANCE]
    }
  }

  return [true, '']
})

// TODO: Maybe worth doing the new tx button?
const SubmitButton = () => {
  const mode = useAtomValue(modeAtom)
  const tokensNeedingApproval = useAtomValue(tokensNeedingApprovalAtom)

  // Show Approve All when in buy mode and approvals are needed
  if (mode === 'buy' && tokensNeedingApproval.length > 0) {
    return <ApproveAllButton />
  }

  return <MintRedeemButton />
}

const MintRedeemButton = () => {
  const chainId = useAtomValue(chainIdAtom)
  const mode = useAtomValue(modeAtom)
  const [amount, setAmount] = useAtom(amountAtom)
  const [actionMsg, setActionMsg] = useState('') // used for toast description
  const {
    data,
    isPending,
    error: signError,
    writeContract,
    reset,
  } = useWriteContract()
  const { isSuccess, error: txError } = useWaitForTransactionReceipt({
    hash: data,
    chainId,
  })
  const requiredAmounts = useAtomValue(assetAmountsMapAtom)
  const [isValid, validationError] = useAtomValue(isValidAtom)
  const wallet = useAtomValue(walletAtom)
  const indexDTF = useAtomValue(indexDTFAtom)
  const version = useAtomValue(indexDTFVersionAtom)

  useEffect(() => {
    if (isSuccess) {
      toast('Transaction successful', {
        description: actionMsg,
        icon: '🎉',
      })
      reset()
      setAmount('')
    }
  }, [isSuccess])

  const handleSubmit = () => {
    if (!isValid || !indexDTF || !wallet) return

    if (mode === 'buy') {
      setActionMsg(
        `${formatCurrency(Number(amount))} ${indexDTF.token.symbol} minted!`
      )

      const shares = safeParseEther(amount)

      if (version === '1.0.0') {
        writeContract({
          address: indexDTF.id,
          abi: dtfIndexAbi,
          functionName: 'mint',
          args: [shares, wallet],
          chainId,
        })
      } else {
        const mintFee = parseEther(indexDTF.mintingFee.toString() || '0')
        const min = parseEther('0.0015')
        const d18 = parseEther('1')
        const minSharesOut = (shares * (d18 - max(mintFee, min)) - 1n) / d18

        writeContract({
          address: indexDTF.id,
          abi: dtfIndexAbiV2,
          functionName: 'mint',
          args: [shares, wallet, minSharesOut],
          chainId,
        })
      }
    } else {
      const assets: Address[] = []
      const minAmounts: bigint[] = []

      for (const asset of Object.keys(requiredAmounts)) {
        assets.push(asset as Address)
        // 5% slippage
        // TODO: This is worst case scenario, but it maybe too much?
        minAmounts.push((requiredAmounts[asset] * 95n) / 100n)
      }

      setActionMsg(
        `${formatCurrency(Number(amount))} ${indexDTF.token.symbol} redeemed!`
      )
      writeContract({
        address: indexDTF.id,
        abi: dtfIndexAbi,
        functionName: 'redeem',
        args: [
          safeParseEther(amount),
          wallet,
          assets as `0x${string}`[],
          minAmounts as bigint[],
        ],
        chainId,
      })
    }
  }

  let label = mode === 'buy' ? 'Mint' : 'Redeem'
  const error = signError || txError
  const isLoading = (isPending || !!data) && !error

  if (isPending) {
    label = 'Please sign in wallet...'
  } else if (data) {
    label = 'Confirming transaction...'
  }
  return (
    <div className="flex flex-col gap-2">
      <TransactionButtonContainer>
        <Button
          disabled={!isValid || isLoading}
          className="gap-2 w-full"
          onClick={handleSubmit}
        >
          {isLoading && <Spinner />}
          {label}
        </Button>
      </TransactionButtonContainer>

      {(validationError || error) && (
        <Alert
          variant={
            validationError === VALIDATION_ERRORS.ALLOWANCE
              ? 'warning'
              : 'destructive'
          }
        >
          <AlertTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />{' '}
            {validationError || 'Transaction failed'}
          </AlertTitle>
          {!!error && !validationError && (
            <AlertDescription className="ml-6">
              {getErrorMessage(error)}
            </AlertDescription>
          )}
        </Alert>
      )}
    </div>
  )
}

const IndexManualIssuance = () => {
  const mode = useAtomValue(modeAtom)
  const { trackClick } = useTrackIndexDTFClick('overview', 'mint')
  return (
    <div>
      <div className="flex flex-col gap-2 border bg-card rounded-3xl p-4 h-fit">
        <ModeSelector />
        <InputBox />
        <SubmitButton />
      </div>
      <div className="mt-4 rounded-3xl border p-4 flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Having issues minting?</p>
        <Link
          to={`../${ROUTES.ISSUANCE}`}
          onClick={() => trackClick('switch_to_zap')}
        >
          <Button variant="muted" size="xs">
            Switch to zap {mode === 'buy' ? 'minting' : 'redeeming'}
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default IndexManualIssuance
